/**
 * The Hymnal 1982's own table of contents, as data.
 *
 * The book is organised into CONTIGUOUS, DISJOINT number ranges — "Advent" is
 * hymns 53–76, not a tag some hymns carry — so the whole structure is ~32 rows of
 * `{ from, to }`. That shape is why this is authored rather than derived:
 *
 * - Nothing in the corpus carries it. `hymnal_1982.json` has first line, author,
 *   translator, meter, language, tune, composer, arranger, harmonizer and key, and
 *   no section. Neither does the sibling `eh1982-metadata.json`, and Canticle's
 *   `HymnalEntry.topics` is declared but unpopulated.
 * - hymnary.org's topic index is the closest public source and is the WRONG SHAPE:
 *   topics are per-hymn tags, multi-valued, and demonstrably incomplete (its
 *   Ascension list omits 214–222's own 215 and 217). It is used as a CHECK on this
 *   table — see `scripts/scrape-hymnary-topics.mjs` and the containment test — and
 *   never as its source.
 *
 * THE SECTION IS ALSO THE SEASON, for hymns 47–293. That is the axis a church
 * musician actually plans on ("what can we sing in Lent?"), and it is the reason
 * this file exists at all. `season` is set only where the section genuinely IS a
 * season: "Holy Days and Various Occasions" is a section, not a season, and
 * pretending otherwise would put Christmas hymns in an Ordinary Time filter.
 *
 * PURE — no React, no I/O, no dataset import. The caller supplies the data, same
 * discipline as `hymnal-pages.ts`.
 */
const CHURCH_YEAR = 'The Church Year';
const GENERAL = 'General Hymns';
/**
 * The Hymnal 1982, hymns 1–720. Contiguous and gapless by construction; the test
 * asserts it rather than trusting it.
 *
 * Service Music (S1–S288) is a separate numbering and is NOT in this table —
 * the 720-entry corpus does not contain it.
 *
 * Every boundary below was cross-checked against the first line the corpus holds
 * at that number: 56 *O come, O come, Emmanuel*, 87 *Hark! the herald angels
 * sing*, 117 *Brightest and best*, 143 *The glory of these forty days*, 154 *Thou
 * art the King of Israel*, 207 *Jesus Christ is risen today*, 214 *Hail the day
 * that sees him rise*, 225 *Lo, in the likeness of fire*, 287 *For all the
 * saints*, 301 *Bread of the world*, 360 *Only begotten, Word of God eternal*,
 * 362 *Holy, holy, holy*, 517 *How lovely is thy dwelling place*, 525 *The
 * Church's one foundation*, 632 *O Christ, the Word Incarnate*, 688 *A mighty
 * fortress*, 715 *When Jesus wept*, 717 *My country, 'tis of thee*.
 */
export const HYMNAL_1982_SECTIONS = [
    { slug: 'daily-office', label: 'The Daily Office', from: 1, to: 46 },
    { slug: 'sunday', label: 'Sunday', group: CHURCH_YEAR, from: 47, to: 52 },
    { slug: 'advent', label: 'Advent', group: CHURCH_YEAR, from: 53, to: 76, season: 'Advent' },
    { slug: 'christmas', label: 'Christmas', group: CHURCH_YEAR, from: 77, to: 115, season: 'Christmas' },
    { slug: 'epiphany', label: 'Epiphany', group: CHURCH_YEAR, from: 116, to: 139, season: 'Epiphany' },
    { slug: 'lent', label: 'Lent', group: CHURCH_YEAR, from: 140, to: 152, season: 'Lent' },
    { slug: 'holy-week', label: 'Holy Week', group: CHURCH_YEAR, from: 153, to: 173, season: 'Holy Week' },
    { slug: 'easter', label: 'Easter', group: CHURCH_YEAR, from: 174, to: 213, season: 'Easter' },
    { slug: 'ascension', label: 'Ascension', group: CHURCH_YEAR, from: 214, to: 222, season: 'Ascension' },
    { slug: 'pentecost', label: 'Pentecost', group: CHURCH_YEAR, from: 223, to: 230, season: 'Pentecost' },
    {
        slug: 'holy-days',
        label: 'Holy Days, the Common of Saints, and Various Occasions',
        group: CHURCH_YEAR,
        from: 231,
        to: 293,
    },
    { slug: 'holy-baptism', label: 'Holy Baptism', from: 294, to: 299 },
    { slug: 'holy-eucharist', label: 'The Holy Eucharist', from: 300, to: 347 },
    { slug: 'confirmation', label: 'Confirmation', from: 348, to: 349 },
    { slug: 'marriage', label: 'Marriage', from: 350, to: 353 },
    { slug: 'burial', label: 'Burial of the Dead', from: 354, to: 358 },
    { slug: 'ordination', label: 'Ordination', from: 359, to: 359 },
    { slug: 'consecration-of-a-church', label: 'Consecration of a Church', from: 360, to: 361 },
    { slug: 'holy-trinity', label: 'The Holy Trinity', group: GENERAL, from: 362, to: 371 },
    { slug: 'praise-to-god', label: 'Praise to God', group: GENERAL, from: 372, to: 433 },
    { slug: 'jesus-christ-our-lord', label: 'Jesus Christ our Lord', group: GENERAL, from: 434, to: 499 },
    { slug: 'holy-spirit', label: 'The Holy Spirit', group: GENERAL, from: 500, to: 516 },
    // ✅ CONFIRMED AGAINST THE PRINTED HYMNAL (Garrett, 2026-08-09): hymn 528,
    // "Lord, you give the great commission", is under **The Church**.
    //
    // Two sources had disagreed. A secondary summary gave The Church 517-527 /
    // Mission 528-544; hymnary's per-hymn topic index tagged 528 as The Church and
    // started Mission at 531, leaving 529-530 untagged. The per-hymn evidence was
    // taken because that index reproduces more than twenty other boundaries EXACTLY
    // (371/372, 516/517, 565/566, 617/618, 634/635 …) — and the book agrees.
    //
    // 528 is now a reviewed anchor in hymnal-sections.test.ts, so this is pinned by
    // a test rather than by this comment.
    { slug: 'the-church', label: 'The Church', group: GENERAL, from: 517, to: 530 },
    { slug: 'church-mission', label: "The Church's Mission", group: GENERAL, from: 531, to: 544 },
    {
        slug: 'christian-vocation-and-pilgrimage',
        label: 'Christian Vocation and Pilgrimage',
        group: GENERAL,
        from: 545,
        to: 565,
    },
    { slug: 'christian-responsibility', label: 'Christian Responsibility', group: GENERAL, from: 566, to: 612 },
    { slug: 'kingdom-of-god', label: 'The Kingdom of God', group: GENERAL, from: 613, to: 617 },
    { slug: 'church-triumphant', label: 'The Church Triumphant', group: GENERAL, from: 618, to: 625 },
    { slug: 'holy-scripture', label: 'Holy Scripture', group: GENERAL, from: 626, to: 634 },
    { slug: 'christian-life', label: 'The Christian Life', from: 635, to: 709 },
    { slug: 'rounds-and-canons', label: 'Rounds and Canons', from: 710, to: 715 },
    { slug: 'national-songs', label: 'National Songs', from: 716, to: 720 },
];
/** Lowest and highest hymn number the table covers. */
export const HYMNAL_1982_RANGE = { first: 1, last: 720 };
/**
 * Numeric part of a hymn number. Hymn numbers are strings and can in principle
 * carry a letter suffix (`"143a"`); the section is decided by the digits.
 */
const numberOf = (value) => {
    const m = /^\s*(\d+)/.exec(String(value ?? ''));
    return m ? Number(m[1]) : Number.NaN;
};
/** The section a hymn number falls in, or `undefined` if the table does not cover it. */
export function sectionOf(sections, number) {
    const n = numberOf(number);
    if (!Number.isFinite(n))
        return undefined;
    return sections.find((s) => n >= s.from && n <= s.to);
}
/** How many hymn numbers a section spans. Not the number of hymns that EXIST in
 *  it — the corpus is the authority on that, and this table is not. */
export const sectionSize = (s) => s.to - s.from + 1;
/** Route for a section page. Lives here with the table so the router, the
 *  prerenderer's `extraRoutes` and the sitemap read one definition. */
export const sectionRoute = (source, slug) => `/hymnal/${source.id}/section/${slug}`;
/**
 * Structural check on a section table: ascending, non-overlapping, gapless, and
 * covering `first..last` exactly.
 *
 * Exported rather than living in the test because it is the definition of a
 * well-formed table — a second hymnal added later (D2 of the SEO plan) gets the
 * same check for free, and a reviewer can read the rule in one place.
 */
export function findSectionDefects(sections, range = HYMNAL_1982_RANGE) {
    const out = [];
    const seen = new Set();
    for (const s of sections) {
        if (seen.has(s.slug))
            out.push({ kind: 'duplicate-slug', detail: s.slug });
        seen.add(s.slug);
        if (s.to < s.from)
            out.push({ kind: 'inverted', detail: `${s.slug} ${s.from}-${s.to}` });
    }
    const sorted = [...sections].sort((a, b) => a.from - b.from);
    if (sorted.length === 0)
        return out;
    if (sorted[0].from !== range.first) {
        out.push({ kind: 'out-of-range', detail: `starts at ${sorted[0].from}, expected ${range.first}` });
    }
    const lastSection = sorted[sorted.length - 1];
    if (lastSection.to !== range.last) {
        out.push({ kind: 'out-of-range', detail: `ends at ${lastSection.to}, expected ${range.last}` });
    }
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        if (cur.from <= prev.to) {
            out.push({
                kind: 'overlap',
                detail: `${prev.slug} (${prev.from}-${prev.to}) / ${cur.slug} (${cur.from}-${cur.to})`,
            });
        }
        else if (cur.from !== prev.to + 1) {
            out.push({ kind: 'gap', detail: `${prev.to + 1}-${cur.from - 1} between ${prev.slug} and ${cur.slug}` });
        }
    }
    return out;
}
